// src/utils/operationCenter/layoutUtils.ts
// Grid layout utilities for Operation Center dashboards

import type { LayoutItem } from "@/types/dashboard";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const GRID_COLS = 12;
export const ROW_HEIGHT = 100;
export const GRID_MARGIN: [number, number] = [16, 16];

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface WidgetDimensions {
  i: string;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Smart Grid Layout Algorithm
// Uses row-filling with height awareness for clean, gap-free layouts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Smart layout algorithm that packs widgets into a clean grid.
 *
 * Strategy:
 * 1. Separate widgets into size categories by width: quarter (w=3), half (w=6), wide (w>=9)
 * 2. Place quarter-width widgets first (they pack well: 4 per row)
 * 3. Then half-width widgets (2 per row)
 * 4. Then wide/full-width widgets (1 per row)
 * 5. Use best-fit row packing to fill each row to exactly 12 columns
 *
 * This creates visually clean layouts without awkward gaps.
 * Standardized grid: widths are 3/6/9/12, heights are 4/8
 */
export function computeSmartLayout(
  widgets: WidgetDimensions[],
  cols: number = GRID_COLS
): LayoutItem[] {
  if (widgets.length === 0) return [];

  // Categorize widgets by width for better row packing
  const quarter: WidgetDimensions[] = []; // w <= 3 (quarter width)
  const half: WidgetDimensions[] = []; // w = 4-6 (half width)
  const wide: WidgetDimensions[] = []; // w >= 7 (wide/full width)

  for (const w of widgets) {
    if (w.w <= 3) {
      quarter.push(w);
    } else if (w.w <= 6) {
      half.push(w);
    } else {
      wide.push(w);
    }
  }

  // Sort each category by width descending for better packing
  quarter.sort((a, b) => b.w - a.w);
  half.sort((a, b) => b.w - a.w);
  wide.sort((a, b) => b.w - a.w);

  const layout: LayoutItem[] = [];
  let currentY = 0;

  // Helper to find best combination of widgets that fills a row
  const findBestRowCombination = (
    widgetList: WidgetDimensions[],
    targetWidth: number
  ): number[] => {
    // Try to find subset that sums to exactly targetWidth
    // Use dynamic programming for small sets, greedy for larger

    if (widgetList.length <= 8) {
      // For small sets, try all combinations to find exact fit
      const n = widgetList.length;
      let bestIndices: number[] = [];
      let bestSum = 0;

      // Try all subsets (2^n combinations)
      for (let mask = 1; mask < (1 << n); mask++) {
        let sum = 0;
        const indices: number[] = [];

        for (let i = 0; i < n; i++) {
          if (mask & (1 << i)) {
            const w = widgetList[i];
            if (w) {
              sum += w.w;
              indices.push(i);
            }
          }
        }

        // Prefer exact match, then closest without going over
        if (sum === targetWidth) {
          return indices; // Perfect fit, use it
        }
        if (sum <= targetWidth && sum > bestSum) {
          bestSum = sum;
          bestIndices = indices;
        }
      }

      return bestIndices;
    }

    // Greedy fallback for larger sets
    const indices: number[] = [];
    let sum = 0;
    for (let i = 0; i < widgetList.length; i++) {
      const w = widgetList[i];
      if (w && sum + w.w <= targetWidth) {
        indices.push(i);
        sum += w.w;
        if (sum === targetWidth) break;
      }
    }
    return indices;
  };

  // Helper to place a group of widgets using optimal row-filling
  const placeWidgetsRowFill = (group: WidgetDimensions[]): void => {
    if (group.length === 0) return;

    const remaining = [...group];

    while (remaining.length > 0) {
      // Find the best combination of widgets to fill this row
      const selectedIndices = findBestRowCombination(remaining, cols);

      if (selectedIndices.length === 0) {
        // Shouldn't happen, but safety fallback - take first widget
        const firstWidget = remaining.shift();
        if (firstWidget) {
          const layoutItem: LayoutItem = {
            i: firstWidget.i,
            x: 0,
            y: currentY,
            w: firstWidget.w,
            h: firstWidget.h,
          };
          if (firstWidget.minW !== undefined) layoutItem.minW = firstWidget.minW;
          if (firstWidget.minH !== undefined) layoutItem.minH = firstWidget.minH;
          if (firstWidget.maxW !== undefined) layoutItem.maxW = firstWidget.maxW;
          if (firstWidget.maxH !== undefined) layoutItem.maxH = firstWidget.maxH;
          layout.push(layoutItem);
          currentY += firstWidget.h;
        }
        continue;
      }

      // Extract selected widgets (in reverse order to maintain indices during removal)
      const row: WidgetDimensions[] = [];
      let rowHeight = 0;

      // Sort indices descending so we can remove from end first
      selectedIndices.sort((a, b) => b - a);
      for (const idx of selectedIndices) {
        const widget = remaining[idx];
        if (widget) {
          row.unshift(widget); // Add to front to maintain original order
          rowHeight = Math.max(rowHeight, widget.h);
          remaining.splice(idx, 1);
        }
      }

      // Sort row by width descending for visual consistency (larger on left)
      row.sort((a, b) => b.w - a.w);

      // Place widgets in this row
      let x = 0;
      for (const widget of row) {
        const layoutItem: LayoutItem = {
          i: widget.i,
          x,
          y: currentY,
          w: widget.w,
          h: widget.h,
        };

        if (widget.minW !== undefined) layoutItem.minW = widget.minW;
        if (widget.minH !== undefined) layoutItem.minH = widget.minH;
        if (widget.maxW !== undefined) layoutItem.maxW = widget.maxW;
        if (widget.maxH !== undefined) layoutItem.maxH = widget.maxH;

        layout.push(layoutItem);
        x += widget.w;
      }

      currentY += rowHeight;
    }
  };

  // Place widgets in order: quarter-width first, then half, then wide
  placeWidgetsRowFill(quarter);
  placeWidgetsRowFill(half);
  placeWidgetsRowFill(wide);

  return layout;
}

/**
 * Find the best position for a single new widget in existing layout.
 * Uses gap-filling to find the first available spot that fits.
 */
export function findBestPosition(
  layout: LayoutItem[],
  widgetW: number,
  widgetH: number,
  cols: number = GRID_COLS
): { x: number; y: number } {
  if (layout.length === 0) {
    return { x: 0, y: 0 };
  }

  // Build a height map: for each column, track the max occupied y
  const columnHeights = new Array(cols).fill(0);

  for (const item of layout) {
    const itemBottom = item.y + item.h;
    for (let col = item.x; col < item.x + item.w && col < cols; col++) {
      columnHeights[col] = Math.max(columnHeights[col], itemBottom);
    }
  }

  // Find the first position where the widget fits
  // Scan from top to bottom, left to right
  const maxY = Math.max(...columnHeights, 0);

  for (let y = 0; y <= maxY + 10; y++) {
    for (let x = 0; x <= cols - widgetW; x++) {
      // Check if this position is clear
      const wouldOverlap = layout.some((item) => {
        const itemRight = item.x + item.w;
        const itemBottom = item.y + item.h;
        const newRight = x + widgetW;
        const newBottom = y + widgetH;

        // Check for overlap
        return !(
          newRight <= item.x ||
          x >= itemRight ||
          newBottom <= item.y ||
          y >= itemBottom
        );
      });

      if (!wouldOverlap) {
        // Also check that we're not placing above the column heights
        // (to maintain top-to-bottom fill pattern)
        const minColumnHeight = Math.min(
          ...columnHeights.slice(x, x + widgetW)
        );
        if (y >= minColumnHeight || y === 0) {
          return { x, y };
        }
      }
    }
  }

  // Fallback: place at bottom
  return { x: 0, y: maxY };
}

/**
 * Create a layout item from widget dimensions at a specific position
 */
export function createLayoutItem(
  widgetId: string,
  position: { x: number; y: number },
  dimensions: { w: number; h: number },
  constraints?: { minW?: number; minH?: number; maxW?: number; maxH?: number }
): LayoutItem {
  const item: LayoutItem = {
    i: widgetId,
    x: position.x,
    y: position.y,
    w: dimensions.w,
    h: dimensions.h,
  };

  if (constraints?.minW !== undefined) item.minW = constraints.minW;
  if (constraints?.minH !== undefined) item.minH = constraints.minH;
  if (constraints?.maxW !== undefined) item.maxW = constraints.maxW;
  if (constraints?.maxH !== undefined) item.maxH = constraints.maxH;

  return item;
}

